import { AgGridReact } from 'ag-grid-react';
import { useMemo } from 'react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
    CellEditingStoppedEvent,
    CellValueChangedEvent,
    ColDef,
    GridReadyEvent,
} from 'ag-grid-community';

const DEFAULT_PAGINATION_SIZE = 15;
const DEFAULT_PAGINATION_SIZE_SELECTOR = [15, 30, 50, 100];
const DEFAULT_COL_DEFS: ColDef = {
    flex: 1,
    sortable: true,
    resizable: true,
    minWidth: 150,
};

// Inline FR locale for AG Grid (avoids @ag-grid-community/locale dep).
const AG_GRID_LOCALE_FR_INLINE = {
    page: 'Page',
    more: 'Plus',
    to: 'à',
    of: 'de',
    next: 'Suivant',
    last: 'Dernier',
    first: 'Premier',
    previous: 'Précédent',
    firstPage: 'Première Page',
    previousPage: 'Page Précédente',
    nextPage: 'Page Suivante',
    lastPage: 'Dernière Page',
    pageLastRowUnknown: '?',
    pageSizeSelectorLabel: 'Taille de la Page :',
    ariaPageSizeSelectorLabel: 'Taille de page',
    loadingOoo: 'Chargement...',
    noRowsToShow: 'Aucune donnée',
    filterOoo: 'Filtrer...',
    equals: 'Égal',
    notEqual: 'Différent',
    lessThan: 'Inférieur à',
    greaterThan: 'Supérieur à',
    contains: 'Contient',
    notContains: 'Ne contient pas',
    startsWith: 'Commence par',
    endsWith: 'Se termine par',
    sortAscending: 'Tri croissant',
    sortDescending: 'Tri décroissant',
    noSort: 'Pas de tri',
};

export type CellValue = string | number | boolean | undefined | null;

interface IDataTable<T> {
    header: ColDef<T>[];
    data: T[] | undefined;
    paginationPageSize?: number;
    isPaginated?: boolean;
    paginationPageSizeSelector?: number[];
    defaultColDef?: ColDef;
    testId: string;
    onCellValueChanged?: (event: CellValueChangedEvent) => void;
    verticalScroll?: boolean;
    dynamicVerticalScroll?: boolean;
    onCellEditingStopped?: (event: CellEditingStoppedEvent) => void;
    onGridReady?: (event: GridReadyEvent) => void;
    pinnedBottomRowData?: T[];
}

type CellParams = {
    readOnly?: boolean;
    selected?: boolean;
};

export function getCellClassName({ readOnly, selected }: CellParams): string[] {
    if (readOnly) {
        return [
            'bg-[var(--nj-semantic-color-background-neutral-tertiary-hovered)]',
        ];
    }

    return selected ? ['table-cell-selected'] : ['cursor-cross-hair'];
}

export type FormatValueKind =
    | 'asIs'
    | 'coordinates'
    | 'default'
    | 'efficiency'
    | 'kpi'
    | 'massEditorValue'
    | 'percentage';

export type FormatValueParams = {
    kind: FormatValueKind;
};

const getNumberOfDigits = (kind: FormatValueKind): number =>
    ['efficiency', 'massEditorValue'].includes(kind) ? 3 : 2;

const formatNumberValue = (value: number, kind: FormatValueKind): string => {
    if (kind === 'efficiency') {
        return value.toFixed(3);
    }

    if (kind === 'coordinates') {
        return value.toFixed(8);
    }

    const numberOfDigits = getNumberOfDigits(kind);

    if (value >= 100_000) {
        return value.toExponential(numberOfDigits);
    }

    if (Number.isInteger(value)) {
        return value.toFixed(0);
    }

    const rounded = Number(value.toFixed(numberOfDigits));
    return Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
};

export const isEmpty = (v: CellValue) =>
    v === null || v === undefined || v === '';
const isNumber = (v: CellValue) => !Number.isNaN(Number(v));

export const formatValue = (
    value: CellValue,
    params?: FormatValueParams
): string => {
    const kind = params?.kind || 'default';

    if (isEmpty(value)) {
        return '-';
    }

    if (kind === 'percentage' && isNumber(value)) {
        return `${formatNumberValue(Number(value) * 100, 'percentage')}%`;
    }

    if (kind === 'coordinates' && isNumber(value)) {
        return formatNumberValue(Number(value), 'coordinates');
    }

    if (typeof value === 'boolean' || kind === 'asIs') {
        return value!.toString();
    }

    const asNumber = Number(value);
    if (isNumber(asNumber)) {
        return formatNumberValue(asNumber, kind);
    }

    return value!.toString();
};

export default function DataTable<T>(props: IDataTable<T>) {
    const {
        data,
        header,
        defaultColDef = DEFAULT_COL_DEFS,
        paginationPageSize = DEFAULT_PAGINATION_SIZE,
        paginationPageSizeSelector = DEFAULT_PAGINATION_SIZE_SELECTOR,
        isPaginated = false,
        verticalScroll = false,
        dynamicVerticalScroll = false,
        onCellValueChanged,
        onCellEditingStopped,
        onGridReady,
        testId,
        pinnedBottomRowData,
    } = props;

    const gridOptions = useMemo(
        () => ({
            localeText: AG_GRID_LOCALE_FR_INLINE,
        }),
        []
    );

    const DEFAULT_HEIGHT = '57vh';
    const offset = isPaginated ? 18 : 7;
    const vHeight = useMemo(() => {
        if (!data?.length) return undefined;

        const rowHeight = 7;
        const headerHeight = 8;
        const paddingHeight = 4;
        const pinnedBottomHeight = pinnedBottomRowData?.length ? 7 : 0;

        const calculatedHeight =
            data.length * rowHeight +
            headerHeight +
            paddingHeight +
            pinnedBottomHeight;

        const maxHeight = 65;

        return Math.min(calculatedHeight, maxHeight) + (isPaginated ? 11 : 0);
    }, [data, isPaginated, pinnedBottomRowData]);

    const SMALL_DATA_THRESHOLD = 9;
    const shouldUseAutoHeight =
        dynamicVerticalScroll &&
        data &&
        data.length > 0 &&
        data.length <= SMALL_DATA_THRESHOLD;

    let domLayout: 'autoHeight' | 'normal';
    if (shouldUseAutoHeight) {
        domLayout = 'autoHeight';
    } else if (verticalScroll || dynamicVerticalScroll) {
        domLayout = 'normal';
    } else {
        domLayout = 'autoHeight';
    }

    const dynamicStyle = useMemo(() => {
        if (!verticalScroll && !dynamicVerticalScroll) return {};
        if (shouldUseAutoHeight) return {};
        if (verticalScroll && !dynamicVerticalScroll)
            return { height: DEFAULT_HEIGHT };

        if (vHeight) {
            return { height: `${vHeight}vh` };
        }
        if (data?.length === 0) {
            return { height: `${offset + 5}vh` };
        }
        return { height: DEFAULT_HEIGHT };
    }, [
        verticalScroll,
        dynamicVerticalScroll,
        shouldUseAutoHeight,
        vHeight,
        data?.length,
        offset,
    ]);

    return (
        <div data-testid={testId} style={dynamicStyle}>
            <AgGridReact
                columnDefs={header}
                rowData={data}
                defaultColDef={defaultColDef}
                gridOptions={gridOptions}
                suppressMovableColumns
                className="ag-theme-quartz mt-4"
                domLayout={domLayout}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                pagination={isPaginated}
                onCellValueChanged={onCellValueChanged}
                enableBrowserTooltips
                onCellEditingStopped={onCellEditingStopped}
                onGridReady={onGridReady}
                pinnedBottomRowData={pinnedBottomRowData}
            />
        </div>
    );
}
